/**
 * Vello message-ID contract (S055; extraction contract §1/§4 `@vello/i18n`, DD-04).
 *
 * Component copy is a **message id**, never an inline string. The design source shipped PT-BR copy inside
 * components (design-debt DD-04: locale leaking into structure); this package is the neutral boundary that
 * copy routes through, so a component references `action.confirm` and a locale catalog supplies the words.
 * There is deliberately **no built-in default locale** (matching `@vello/format`'s `resolveLocale`, C-13):
 * the catalog carries its own `locale`, and plural selection is driven by that locale via `Intl.PluralRules`.
 *
 * Missing keys **fail soft, visibly**: `translate` returns the id and `resolved: false` rather than throwing
 * (a screen never crashes on a missing string), and a QA sweep can assert every id resolved.
 */
/** Interpolate `{name}` placeholders from `vars`. A missing var leaves the placeholder untouched. */
function interpolate(template, vars) {
    if (!vars)
        return template;
    return template.replace(/\{(\w+)\}/g, (match, key) => Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match);
}
/**
 * Resolve a message id against a catalog. Falls back to the descriptor's `default` (if provided) or the id
 * itself, always reporting whether the lookup resolved. Never throws — a screen degrades to the id, not a
 * crash.
 */
export function translate(catalog, id, vars) {
    const descriptor = typeof id === 'string' ? { id } : id;
    const raw = catalog.messages[descriptor.id];
    if (raw !== undefined)
        return { text: interpolate(raw, vars), resolved: true };
    const fallback = descriptor.default ?? descriptor.id;
    return { text: interpolate(fallback, vars), resolved: false };
}
/**
 * The CLDR plural category for a count in a locale — driven by the catalog's locale, never hardcoded. Hermes (the
 * React Native engine on Android) ships no `Intl.PluralRules` - `new` on it threw "undefined cannot be used as a
 * constructor" and took down every provider screen with a counted string (D750, the offer card on a real phone). Without
 * it, the two-form rule (`one` for exactly 1, else `other`) is the fallback: never a crash, at worst a plain plural.
 */
export function selectPlural(locale, count) {
    if (typeof Intl === 'object' && typeof Intl.PluralRules === 'function') {
        try {
            return new Intl.PluralRules(locale).select(count);
        }
        catch {
            /* an unknown locale tag on a partial runtime: the fallback below */
        }
    }
    return Math.abs(count) === 1 ? 'one' : 'other';
}
/**
 * Resolve a pluralized message. The catalog holds `${baseId}.${category}` keys (e.g. `stops.count.one`,
 * `stops.count.other`); the CLDR category comes from the catalog's locale so a language with more than two
 * plural forms (Arabic, Russian, …) selects correctly with no code change. Falls back to `.other`, then the
 * base id. `{count}` is available to the template.
 */
export function translatePlural(catalog, baseId, count, vars) {
    const category = selectPlural(catalog.locale, count);
    const withCount = { count, ...(vars ?? {}) };
    const primary = catalog.messages[`${baseId}.${category}`];
    if (primary !== undefined)
        return { text: interpolate(primary, withCount), resolved: true };
    const other = catalog.messages[`${baseId}.other`];
    if (other !== undefined)
        return { text: interpolate(other, withCount), resolved: true };
    return { text: baseId, resolved: false };
}
/**
 * The neutral message ids the shared design-system primitives need (generic action/state/field copy). Product
 * screens declare their own ids in their own packages; these are the cross-cutting ones the components carry.
 */
export const UI_MESSAGE_IDS = {
    actionConfirm: 'ui.action.confirm',
    actionCancel: 'ui.action.cancel',
    actionClose: 'ui.action.close',
    actionRetry: 'ui.action.retry',
    actionBack: 'ui.action.back',
    stateLoading: 'ui.state.loading',
    stateEmpty: 'ui.state.empty',
    stateError: 'ui.state.error',
    stateOffline: 'ui.state.offline',
    fieldOptional: 'ui.field.optional',
    fieldRequired: 'ui.field.required',
};
/** Every id a component might reference. A catalog is "complete for the UI" when it covers these. */
export function missingUiMessageIds(catalog) {
    return Object.values(UI_MESSAGE_IDS).filter((id) => catalog.messages[id] === undefined);
}
//# sourceMappingURL=messages.js.map